import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { getFirebaseAuth, onAuthStateChanged, signInWithGoogle, signOutCurrentUser } from '../services/firebase'
import { apiService, ApiError } from '../services/api'
import type { TenantRole } from '../types'

type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated' | 'denied'

interface AuthState {
  status: AuthStatus
  email: string | null
  slug: string | null
  /** Tenant role from /api/auth/me. Gates the user-management screen. */
  role: TenantRole | null
  /** Cross-tenant platform operator flag from /api/auth/me. */
  platformAdmin: boolean
  /** Non-fatal sign-in error key for the UI (e.g. 'popup_blocked'). */
  error: string | null
}

const SIGNED_OUT: AuthState = {
  status: 'unauthenticated',
  email: null,
  slug: null,
  role: null,
  platformAdmin: false,
  error: null,
}

interface AuthContextType extends AuthState {
  /** Start Google popup sign-in. MUST be called synchronously from a click. */
  login: () => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

/** Firebase-backed provider, used on resolved tenant hosts. */
function AuthProviderInner({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ ...SIGNED_OUT, status: 'loading' })

  // Subscribe to Firebase auth state. On sign-in, probe /api/auth/me to check
  // this account is authorized for the tenant (200 → authenticated, 403 → denied).
  useEffect(() => {
    const auth = getFirebaseAuth()
    if (!auth) {
      setState(SIGNED_OUT)
      return
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState(SIGNED_OUT)
        return
      }
      try {
        const me = await apiService.getMe()
        setState({
          status: 'authenticated',
          email: me.email ?? user.email ?? null,
          slug: me.slug ?? null,
          // Absent/unknown role degrades to 'user': admin UI is opt-in, never a default.
          role: me.role === 'admin' ? 'admin' : 'user',
          platformAdmin: me.platform_admin === true,
          error: null,
        })
      } catch (e) {
        if (e instanceof ApiError && e.statusCode === 403) {
          setState({ ...SIGNED_OUT, status: 'denied', email: user.email ?? null })
        } else {
          setState({ ...SIGNED_OUT, error: 'login_failed' })
        }
      }
    })

    return unsub
  }, [])

  // Popup is opened synchronously here (from the click handler) to dodge blockers.
  const login = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }))
    signInWithGoogle().catch((e: unknown) => {
      const code = (e as { code?: string })?.code ?? ''
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return
      }
      const errorKey = code === 'auth/popup-blocked' ? 'popup_blocked' : 'login_failed'
      setState((prev) => ({ ...prev, error: errorKey }))
    })
  }, [])

  const logout = useCallback(async () => {
    try {
      await signOutCurrentUser()
    } catch {
      // ignore; onAuthStateChanged drives the state transition
    }
    // Stay on the current subdomain; the gate re-renders LoginPage.
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Provider entry point. `active` = firebase-backed tenant mode. When inactive
 * (dev / apex), auth is a no-op and the user is treated as authenticated so the
 * dev dashboard renders without a sign-in gate.
 */
export function AuthProvider({ active, children }: { active: boolean; children: ReactNode }) {
  if (!active) {
    const passthrough: AuthContextType = {
      status: 'authenticated',
      email: null,
      slug: null,
      // Dev / no-auth mode is a trusted local context: surface the admin screen.
      // The backend remains the authority (it 403s if the caller is not an admin).
      role: 'admin',
      platformAdmin: false,
      error: null,
      login: () => {},
      logout: async () => {},
    }
    return <AuthContext.Provider value={passthrough}>{children}</AuthContext.Provider>
  }

  return <AuthProviderInner>{children}</AuthProviderInner>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
